import type { Skill } from "@shared/entities";
import { ELEMENTS, ELEMENT_IDENTITY, ELEMENT_LABEL, HOOK_LABEL, SKILL_RARITIES } from "@shared/game";
import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import { Implemented, RarityText, StatusDot } from "../components/chips";
import { LoadError, Loading, PageHeader } from "../components/page-state";
import { fillDesc, pct } from "../lib/format";
import { boonsQuery, gameTableQuery, skillsQuery } from "../lib/queries";

export const Route = createFileRoute("/skills/")({ component: SkillsPage });

/**
 * The skill board: one column per element, in the game's element order. A
 * skill's five rarity values sit in a row so a designer can read a whole
 * element's power curve down the column.
 */
function SkillsPage() {
	const skills = useQuery(skillsQuery);
	const boons = useQuery(boonsQuery);
	const meta = useQuery(gameTableQuery("skills"));

	if (skills.isPending) return <Loading what="Loading skills" />;
	if (skills.isError) return <LoadError error={skills.error} retry={() => void skills.refetch()} />;

	const byElement = new Map(ELEMENTS.map((el) => [el, skills.data.filter((s) => s.element === el)]));
	const rates = (meta.data?.data as { offer_rates?: Record<string, number>; max_held?: number } | undefined) ?? {};

	return (
		<div className="space-y-8">
			<PageHeader title="Skills and elements" eyebrow={`${skills.data.length} skills across ${ELEMENTS.length} elements`} />
			<div className="max-w-2xl space-y-2" style={{ color: "var(--color-ink-300)" }}>
				<p>
					Every skill belongs to an element and is picked in a Skill Selection: three different skills, one rarity for all three.
					Picking a held Common, Rare or Epic skill again levels it (×1.5, then ×2). Legendary and Mythic are level 1 only.
					{rates.max_held && ` Up to ${rates.max_held} held per run.`}
				</p>
				{rates.offer_rates && (
					<p className="text-sm">
						Selection rarity odds:{" "}
						{SKILL_RARITIES.map((r, i) => (
							<span key={r}>
								{i > 0 && " · "}
								<RarityText rarity={r} /> <span className="num">{pct(rates.offer_rates?.[r] ?? 0)}</span>
							</span>
						))}
					</p>
				)}
			</div>

			<div className="space-y-6">
				{ELEMENTS.map((el) => (
					<section key={el} className="panel overflow-hidden">
						<header className="flex flex-wrap items-baseline gap-3 px-5 py-3" style={{ borderBottom: `1px solid var(--color-ink-700)`, borderLeft: `4px solid var(--color-${el})` }}>
							<h2 className="text-lg" style={{ color: `var(--color-${el})` }}>
								{ELEMENT_LABEL[el]}
							</h2>
							<span className="text-sm" style={{ color: "var(--color-ink-300)" }}>
								{ELEMENT_IDENTITY[el]}
							</span>
						</header>
						<div className="overflow-x-auto">
							<table className="table">
								<thead>
									<tr>
										<th>Skill</th>
										<th>Trigger</th>
										<th>Effect</th>
										{SKILL_RARITIES.map((r) => (
											<th key={r} className="num">
												<RarityText rarity={r}>{r.slice(0, 1).toUpperCase()}</RarityText>
											</th>
										))}
										<th className="num">Max Lv</th>
										<th>Game</th>
										<th>Notes</th>
									</tr>
								</thead>
								<tbody>
									{(byElement.get(el) ?? []).map((s) => (
										<SkillRow key={s.id} skill={s} />
									))}
								</tbody>
							</table>
						</div>
					</section>
				))}
			</div>

			{boons.data && boons.data.length > 0 && (
				<section className="panel p-5">
					<h2 className="mb-1 text-base">Boons</h2>
					<p className="mb-3 text-sm" style={{ color: "var(--color-ink-300)" }}>
						Offered at the Boon Selection stages, pick 1 of {boons.data.length}. Not skills: they never count toward the held limit.
					</p>
					<ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-4">
						{boons.data.map((b) => (
							<li key={b.id} className="rounded-md p-3" style={{ background: "var(--color-ink-800)" }}>
								<div className="font-medium">{b.name}</div>
								<div className="text-sm" style={{ color: "var(--color-ink-300)" }}>
									{b.effect_type === "heal_pct" ? `Heal ${pct(b.value)} max HP now` : `+${pct(b.value)} ${String(b.params["stat"] ?? "").toUpperCase()}`}
								</div>
							</li>
						))}
					</ul>
				</section>
			)}
		</div>
	);
}

function SkillRow({ skill: s }: { skill: Skill }) {
	return (
		<tr style={{ opacity: s.implemented ? 1 : 0.7 }}>
			<td>
				<Link to="/skills/$id" params={{ id: s.id }} className="font-medium underline decoration-[var(--color-ink-600)] underline-offset-2 hover:decoration-[var(--color-gold-400)]">
					{s.name}
				</Link>
			</td>
			<td className="text-xs whitespace-nowrap" style={{ color: "var(--color-ink-300)" }}>
				{s.hook === "every_n_attacks" ? `Every ${ordinal(Number(s.params["n"]))} attack` : HOOK_LABEL[s.hook]}
			</td>
			<td className="min-w-[16rem] text-sm">{fillDesc(s.desc, s.value_by_rarity.common)}</td>
			{SKILL_RARITIES.map((r) => (
				<td key={r} className="num whitespace-nowrap text-sm">
					{pct(s.value_by_rarity[r])}
				</td>
			))}
			<td className="num">{s.max_level}</td>
			<td className="whitespace-nowrap">
				<Implemented value={s.implemented} />
			</td>
			<td>
				<span className="inline-flex items-center gap-2">
					<StatusDot status={s.annotation.status} label={false} />
					<span className="max-w-[14rem] truncate text-sm" style={{ color: s.annotation.summary ? undefined : "var(--color-ink-400)" }}>
						{s.annotation.summary || "—"}
					</span>
				</span>
			</td>
		</tr>
	);
}

const ordinal = (n: number): string => {
	if (!Number.isFinite(n)) return "Nth";
	const s = ["th", "st", "nd", "rd"] as const;
	const v = n % 100;
	return `${n}${s[(v - 20) % 10] ?? s[v] ?? s[0]}`;
};
